import React, { useEffect, useState } from 'react'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import styled, { css } from 'styled-components'
import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg'
import { isNotNilOrEmpty } from 'utils/ramda'
import Tooltip from '@mui/material/Tooltip'

export const SelectMui = props => {
  const {
    options = [],
    name = '',
    label = '',
    onChange = () => {},
    validate = null,
    value: initialValue,
    required = false,
    variant = 'outlined',
    disabled = false,
    placeholder = '',
    withDelete = false,
    onDelete = () => {},
    noMargin = false,
    multiple = false,
    disabledValues = null,
    hasError,
    selectAll,
    disableMobileMargin = false
  } = props
  const emptyValue = multiple ? [] : ''
  const hasNoSelection = selected =>
    selected === undefined ||
    selected === null ||
    selected === '' ||
    (Array.isArray(selected) && selected.length === 0)

  const [value, _setValue] = useState(
    hasNoSelection(initialValue) ? emptyValue : initialValue
  )
  const [touched, _setTouched] = useState(false)
  const [{ valid, error }, _validate] = useState({
    valid: !validate,
    error: ''
  })

  useEffect(() => {
    validate &&
      validate(name, v => {
        _validate({ valid: v.valid, error: v.errors })
      })
  }, [value])

  const renderOptions = options.map(option => {
    return isNotNilOrEmpty(option.description) ? (
      <MenuItem
        key={option.value}
        value={option.value}
        disabled={disabledValues && disabledValues.includes(option.label)}
      >
        <Tooltip title={option.description}>
          <div style={{ width: '100%', height: '100%' }}>{option.label}</div>
        </Tooltip>
      </MenuItem>
    ) : (
      <MenuItem
        key={option.value}
        value={option.value}
        disabled={disabledValues && disabledValues.includes(option.label)}
      >
        {option.label}
      </MenuItem>
    )
  })

  const handleBlur = () => _setTouched(true)
  const handleChange = e => {
    const val = e.target.value

    if (multiple && Array.isArray(val) && val.includes('all')) {
      const allOptions = options.map(el => el.value)
      _setValue(allOptions)
      onChange(name, allOptions)
    } else {
      _setValue(e.target.value)
      onChange(name, e.target.value)
    }
  }

  useEffect(() => {
    _setValue(hasNoSelection(initialValue) ? emptyValue : initialValue)
  }, [initialValue])

  const mobileMenuStyles = disableMobileMargin
    ? {
        marginTop: '2px',
        maxWidth: '100% !important',
        right: 'unset !important',
        margin: '0 !important'
      }
    : {
        width: '100% !important',
        maxWidth: 'calc(100vw - 32px) !important',
        margin: '4px 16px !important'
      }

  return (
    <Wrapper>
      <LabelWrapper>
        {isNotNilOrEmpty(label) && (
          <Label>
            {valid || (!valid && !touched) || disabled ? (
              label
            ) : (
              <Error>{error}</Error>
            )}
          </Label>
        )}
        {withDelete && (
          <DelIconWrapper onClick={onDelete}>
            <span>DELETE</span> <DeleteIcon />
          </DelIconWrapper>
        )}
      </LabelWrapper>
      <StyledFormControl
        noMargin={noMargin}
        variant={variant}
        required={required}
        error={((!valid && touched) || hasError) && !disabled}
      >
        <StyledSelect
          onBlur={handleBlur}
          value={value}
          multiple={multiple}
          onChange={handleChange}
          placeholder={placeholder}
          displayEmpty
          disabled={disabled}
          MenuProps={{
            style: {
              zIndex: 100000
            },
            PaperProps: {
              style: {
                zIndex: 100000,
                maxHeight: '300px'
              },
              sx: {
                '@media (max-width: 768px)': mobileMenuStyles
              }
            },
            disablePortal: false,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            },
            slotProps: {
              paper: {
                sx: {
                  '@media (max-width: 768px)': mobileMenuStyles
                }
              }
            }
          }}
          renderValue={selected => {
            if (hasNoSelection(selected)) {
              return <PlaceholderText>{placeholder}</PlaceholderText>
            }
            if (multiple) {
              const selectedOptions = options.filter(opt =>
                selected.includes(opt.value)
              )
              const labels = selectedOptions.map(opt => opt.label)

              if (labels.length <= 3) {
                return labels.join(', ')
              }

              return `${labels.slice(0, 3).join(', ')} +${labels.length - 3} more`
            }

            const selectedOption = options.find(opt => opt.value === selected)
            return selectedOption ? selectedOption.label : selected
          }}
        >
          {selectAll && multiple && (
            <MenuItem value={'all'}>Select all</MenuItem>
          )}
          {renderOptions}
        </StyledSelect>
      </StyledFormControl>
    </Wrapper>
  )
}

export default SelectMui

const Wrapper = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors.secondary};
  }
  .MuiPopover-root {
    z-index: 100000 !important;
  }

  .MuiMenu-paper {
    z-index: 100000 !important;

    @media (max-width: 768px) {
      margin: 8px 0 !important;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
      max-height: 300px !important;
      overflow-y: auto;
    }
  }

  .MuiMenuItem-root {
    @media (max-width: 768px) {
      padding: 12px 16px !important;
      min-height: 48px;
      border-radius: 6px;
      margin: 4px 8px !important;
    }
  }
`

const StyledSelect = styled(Select)`
  box-sizing: border-box;
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.colors.lightGrey : theme.colors.white};
  width: 100% !important;

  input {
    height: 40px;
    width: 90%;
  }

  div {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &.MuiOutlinedInput-root {
    border-radius: 6px;
    width: 100% !important;
    margin: 0 !important;

    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.colors.grey[300]};
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.colors.primary}40;
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.colors.primary};
      border-width: 1px;
    }

    @media (max-width: 768px) {
      border-radius: 6px;
    }
  }
`

const StyledFormControl = styled(FormControl)`
  width: 100%;
  margin-bottom: ${({ noMargin }) => (noMargin ? 0 : '22px')}!important;
  box-sizing: border-box;

  .MuiOutlinedInput-input {
    box-sizing: border-box;
    padding: 10px 12px;
    min-height: 20px !important;
    height: auto !important;

    &:focus {
      background-color: ${({ theme }) => theme.colors.white};
    }

    @media (max-width: 768px) {
      padding: 12px 14px;
    }
  }

  .MuiInputBase-root {
    min-height: 42px;

    @media (max-width: 768px) {
      min-height: 48px;
    }
  }
`

const Label = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  font-weight: bold;
  font-size: 12px;
  letter-spacing: 0.11px;
  min-height: 17px;
`

const Error = styled.span`
  color: ${({ theme }) => theme.colors.error};
`

const LabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  align-items: center;

  ${({ withDelete }) =>
    withDelete &&
    css`
      color: ${({ theme }) => theme.colors.primary};
    `}
`

const DelIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 7px;
  cursor: pointer;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};

  span {
    transition: all 0.3s;
    opacity: 0;
  }

  &:hover {
    span {
      opacity: 1;
    }
  }
`

const PlaceholderText = styled.span`
  color: ${({ theme }) => theme.colors.grey[500]};
  font-weight: 400;
`
